import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { gotoSmsApi } from "./gotoSmsApi";

export const getGotoConversation = createAsyncThunk(
  "gotoSms/getConversation",
  async ({ customerId, before }) => {
    try {
      const data = await gotoSmsApi.getConversation({
        customer_id: customerId,
        before: before || undefined,
        limit: 30,
      });
      return { customerId, before: before || null, data };
    } catch (error) {
      toast.error(error?.message || "Failed to load conversation");
      return { customerId, before: before || null, data: null };
    }
  }
);

export const sendGotoSms = createAsyncThunk(
  "gotoSms/send",
  async ({ customerId, formData }) => {
    try {
      const row = await gotoSmsApi.send(formData);
      return { customerId, row };
    } catch (error) {
      toast.error(error?.message || "Failed to send message");
      return { customerId, row: null };
    }
  }
);

export const getGotoInbox = createAsyncThunk(
  "gotoSms/getInbox",
  async (arg = {}, { getState }) => {
    const filters = getState().gotoSmsStore.inboxFilters;
    try {
      return await gotoSmsApi.getInbox({
        search: filters.search || undefined,
        staff_id: filters.staff_id || undefined,
        quick: filters.quick === "all" ? undefined : filters.quick,
        // ?sync=1 forces a live GoTo pull first (endpoint 1). Only the initial
        // board load / explicit refresh should sync — filter changes re-query
        // locally, not against GoTo.
        ...(arg?.sync ? { sync: 1 } : {}),
      });
    } catch (error) {
      toast.error(error?.message || "Failed to load conversations");
      return null;
    }
  }
);

export const markGotoRead = createAsyncThunk("gotoSms/markRead", async (customerId) => {
  try {
    await gotoSmsApi.markRead(customerId);
  } catch (error) {
    // Non-fatal: unread reconciliation job (spec §4.6) will catch up.
  }
  return customerId;
});

// Sidebar unread badge. Returns { count, provider_id }; provider_id also tells
// the sidebar which goto-inbox channel to subscribe to for live updates.
export const getGotoUnreadCount = createAsyncThunk(
  "gotoSms/unreadCount",
  async () => {
    try {
      return await gotoSmsApi.unreadCount();
    } catch (error) {
      return null;
    }
  }
);

const emptyConversation = () => ({
  messages: [],
  has_more: false,
  oldest_cursor: null,
  no_phone: false,
});

// Update the board row's preview + unread when a message lands. Sorting is
// done at render time (always latest-message desc, per spec §6).
const bumpInboxRow = (state, customerId, message, setUnread) => {
  const row = state.inbox?.rows?.find(
    (r) => String(r.user_id) === String(customerId)
  );
  if (!row) return;
  row.last_message = {
    direction: message.direction,
    body: message.body,
    created_at: message.created_at,
  };
  if (setUnread) row.unread = true;
  else row.unread = false;
};

export const gotoSmsSlice = createSlice({
  name: "gotoSmsStore",
  initialState: {
    conversations: {}, // keyed by customer_id
    loading: false,
    sending: false,
    latestRequest: null,
    inbox: null, // { rows, has_more }
    inboxLoading: false,
    inboxLatestRequest: null,
    inboxFilters: { search: "", staff_id: null, quick: "all" },
    unreadTotal: 0, // sidebar badge — # of unread GoTo conversations
    providerId: null, // resolved clinic id for the goto-inbox channel
  },
  reducers: {
    updateInboxFilters: (state, action) => {
      state.inboxFilters = { ...state.inboxFilters, ...action.payload };
    },
    // Live inbound (Pusher / mock simulator). Dedupes by message id and
    // lazily creates the conversation so a failed initial fetch can't
    // swallow messages. `seen: true` = the thread is open on screen, so the
    // unread dot never flashes for it.
    receiveGotoMessage: (state, action) => {
      const { customerId, message, seen } = action.payload;
      if (!state.conversations[customerId]) {
        state.conversations[customerId] = emptyConversation();
      }
      const convo = state.conversations[customerId];
      if (convo.messages.some((m) => m.id === message.id)) return;
      convo.messages.push(message);
      bumpInboxRow(state, customerId, message, message.direction === "inbound" && !seen);
    },
    // Tenant-wide board event (spec §6.2): bump the row for any
    // conversation, even one that isn't selected/subscribed. When the event
    // carries a customer summary `row` and the conversation isn't listed yet
    // (brand-new thread), insert it so it appears live.
    receiveInboxEvent: (state, action) => {
      const { customer_id, message, row } = action.payload;
      const exists = state.inbox?.rows?.some(
        (r) => String(r.user_id) === String(customer_id)
      );
      if (!exists && row && state.inbox?.rows) {
        state.inbox.rows.push({
          ...row,
          user_id: customer_id,
          unread: message.direction === "inbound",
          last_message: {
            direction: message.direction,
            body: message.body,
            created_at: message.created_at,
          },
        });
        return;
      }
      bumpInboxRow(state, customer_id, message, message.direction === "inbound");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGotoConversation.pending, (state, action) => {
        state.loading = true;
        state.latestRequest = action.meta.requestId;
      })
      .addCase(getGotoConversation.fulfilled, (state, action) => {
        if (action.meta.requestId === state.latestRequest) state.loading = false;
        const { customerId, before, data } = action.payload;
        if (!data) return;
        const existing = state.conversations[customerId] || emptyConversation();
        state.conversations[customerId] = {
          // Older pages prepend; a fresh load replaces.
          messages: before ? [...data.messages, ...existing.messages] : data.messages,
          has_more: !!data.has_more,
          oldest_cursor: data.oldest_cursor || null,
          no_phone: !!data.no_phone,
        };
      })
      .addCase(getGotoConversation.rejected, (state, action) => {
        if (action.meta.requestId === state.latestRequest) state.loading = false;
      })
      .addCase(sendGotoSms.pending, (state) => {
        state.sending = true;
      })
      .addCase(sendGotoSms.fulfilled, (state, action) => {
        state.sending = false;
        const { customerId, row } = action.payload;
        if (!row) return;
        if (!state.conversations[customerId]) {
          state.conversations[customerId] = emptyConversation();
        }
        const convo = state.conversations[customerId];
        if (!convo.messages.some((m) => m.id === row.id)) {
          convo.messages.push(row);
        }
        bumpInboxRow(state, customerId, row, false);
      })
      .addCase(sendGotoSms.rejected, (state) => {
        state.sending = false;
      })
      .addCase(getGotoInbox.pending, (state, action) => {
        state.inboxLoading = true;
        state.inboxLatestRequest = action.meta.requestId;
      })
      .addCase(getGotoInbox.fulfilled, (state, action) => {
        if (action.meta.requestId === state.inboxLatestRequest)
          state.inboxLoading = false;
        if (action.payload) state.inbox = action.payload;
      })
      .addCase(getGotoInbox.rejected, (state, action) => {
        if (action.meta.requestId === state.inboxLatestRequest)
          state.inboxLoading = false;
      })
      .addCase(markGotoRead.pending, (state, action) => {
        // Optimistic: clear the badge the instant the chat is opened, without
        // waiting for the mark-read request to round-trip. action.meta.arg is
        // the customerId passed to the thunk. The `row.unread` guard keeps
        // repeat calls (2-min poll, live sub) from decrementing more than once.
        const customerId = action.meta.arg;
        const row = state.inbox?.rows?.find(
          (r) => String(r.user_id) === String(customerId)
        );
        if (row && row.unread) {
          row.unread = false;
          state.unreadTotal = Math.max(0, state.unreadTotal - 1);
        } else if (row) {
          row.unread = false;
        }
      })
      .addCase(getGotoUnreadCount.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.unreadTotal = Number(action.payload.count) || 0;
        if (action.payload.provider_id != null)
          state.providerId = action.payload.provider_id;
      });
  },
});

export const { receiveGotoMessage, receiveInboxEvent, updateInboxFilters } =
  gotoSmsSlice.actions;
export default gotoSmsSlice.reducer;
